import { Check, Copy, MailPlus, Pencil, Plus, X } from 'lucide-react';
import { Avatar } from '../components/Avatar';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { Invitation, InvitationSurvey, InvitationSurveyAccess, Role, RoleDef, SurveySummary, User } from '../api/types';
import { useAuth } from '../auth';
import { Topbar } from '../components/Layout';
import { confirmAction, Empty, Field, Modal, PageLoader, useToast } from '../components/ui';
import { formatDate } from '../format';
import { useI18n } from '../i18n';
import { roleDescription, roleLabel } from '../roles';

export function inviteUrl(token: string) {
  return `${window.location.origin}/register?invite=${encodeURIComponent(token)}`;
}

function isFull(inv: Invitation) {
  return inv.maxUses !== null && inv.useCount >= inv.maxUses;
}

/** ISO time → value for <input type="datetime-local"> in the browser's zone. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Invitation links: each grants a chosen role, can be capped, and every sign-up needs an admin's approval. */
export function InvitationsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const { t } = useI18n();
  const [invitations, setInvitations] = useState<Invitation[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [reviewing, setReviewing] = useState<Invitation | null>(null);
  const [editing, setEditing] = useState<Invitation | null>(null);
  const [roles, setRoles] = useState<RoleDef[]>([]);

  const load = useCallback(() => {
    api.listInvitations().then((r) => setInvitations(r.invitations), (e) => toast(e instanceof ApiError ? e.message : t('invitations.load_failed'), 'error'));
  }, [toast, t]);

  useEffect(load, [load]);
  useEffect(() => {
    api.roles().then((r) => setRoles(r.roles), (e) => toast(e instanceof ApiError ? e.message : t('roles.load_failed'), 'error'));
  }, [toast, t]);

  if (!can('users.manage')) return <Navigate to="/" replace />;
  const nameOf = (key: Role) => roleLabel(t, key, roles.find((r) => r.key === key)?.name);

  async function copy(inv: Invitation, done = t('invitations.link_copied')) {
    const url = inviteUrl(inv.token);
    try {
      await navigator.clipboard.writeText(url);
      toast(done);
    } catch {
      toast(t('invitations.copy_failed', { url }), 'error');
    }
  }

  async function update(inv: Invitation, patch: Partial<{ maxUses: number; revoked: boolean }>) {
    try {
      await api.updateInvitation(inv.id, patch);
      toast(t('invitations.updated'));
      load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('invitations.update_failed'), 'error');
    }
  }

  return (
    <>
      <Topbar title={t('invitations.title')}>
        <button className="btn primary" onClick={() => setCreating(true)}>
          <Plus /> {t('invitations.add')}
        </button>
      </Topbar>

      <div className="card">
        <div className="card-head">
          <div className="tile">
            <MailPlus />
          </div>
          <div className="grow">
            <h2>{t('invitations.title')}</h2>
            <div className="sub">{t('invitations.sub')}</div>
          </div>
        </div>
        {invitations === null ? (
          <PageLoader />
        ) : invitations.length === 0 ? (
          <Empty icon={<MailPlus />} title={t('invitations.none')}>
            {t('invitations.none_hint')}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('invitations.col_label')}</th>
                  <th>{t('invitations.col_role')}</th>
                  <th>{t('invitations.col_uses')}</th>
                  <th>{t('invitations.col_pending')}</th>
                  <th>{t('invitations.col_status')}</th>
                  <th aria-label={t('surveys.col_actions')} />
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      <div className="title-cell">{inv.label || t('invitations.untitled')}</div>
                      <div className="muted small">{formatDate(inv.createdAt)}</div>
                    </td>
                    <td>{nameOf(inv.role)}</td>
                    <td>
                      {inv.useCount} / {inv.maxUses ?? t('invitations.unlimited')}
                      {inv.expiresAt && (
                        <div className={`small ${inv.expired ? 'error-text' : 'muted'}`}>
                          {t(inv.expired ? 'invitations.expired_on' : 'invitations.expires_on', { date: formatDate(inv.expiresAt, true) })}
                        </div>
                      )}
                      {inv.surveys.length > 0 && (
                        <div className="small muted ellipsis" style={{ maxWidth: 240 }} title={inv.surveys.map((x) => x.title).join(', ')}>
                          {t('invitations.joins', { list: inv.surveys.map((x) => x.title).join(', ') })}
                        </div>
                      )}
                    </td>
                    <td>
                      {inv.pendingCount > 0 ? (
                        <button className="pill dot orange" style={{ cursor: 'pointer', border: 0 }} onClick={() => setReviewing(inv)}>
                          {inv.pendingCount}
                        </button>
                      ) : (
                        <span className="muted">0</span>
                      )}
                    </td>
                    <td>
                      {inv.revoked ? (
                        <span className="pill dot red">{t('invitations.status_revoked')}</span>
                      ) : inv.expired ? (
                        <span className="pill dot">{t('invitations.status_expired')}</span>
                      ) : isFull(inv) ? (
                        <span className="pill dot">{t('invitations.status_full')}</span>
                      ) : (
                        <span className="pill dot green">{t('invitations.status_active')}</span>
                      )}
                    </td>
                    <td className="num">
                      <div className="row gap-8" style={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button className="btn sm" onClick={() => void copy(inv)} disabled={inv.revoked}>
                          <Copy /> {t('invitations.copy_link')}
                        </button>
                        <button className="btn sm" onClick={() => setReviewing(inv)}>
                          {t('invitations.review')}
                        </button>
                        <button className="btn sm" onClick={() => setEditing(inv)}>
                          <Pencil /> {t('invitations.edit')}
                        </button>
                        {inv.revoked ? (
                          <button className="btn sm" onClick={() => void update(inv, { revoked: false })}>
                            {t('invitations.reactivate')}
                          </button>
                        ) : (
                          <button className="btn sm danger" onClick={() => confirmAction(t('invitations.confirm_revoke')) && void update(inv, { revoked: true })}>
                            {t('invitations.revoke')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && (
        <CreateInvitation
          roles={roles}
          onClose={() => setCreating(false)}
          onCreated={(inv) => {
            setCreating(false);
            load();
            void copy(inv, t('invitations.created'));
          }}
        />
      )}
      {editing && (
        <EditInvitation
          invitation={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast(t('invitations.updated'));
            load();
          }}
        />
      )}
      {reviewing && <Signups invitation={reviewing} onClose={() => setReviewing(null)} onChanged={load} />}
    </>
  );
}

function CreateInvitation({ roles, onClose, onCreated }: { roles: RoleDef[]; onClose: () => void; onCreated: (inv: Invitation) => void }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ label: '', role: 'enumerator' as Role, maxUses: '', expiresAt: '' });
  const [surveys, setSurveys] = useState<InvitationSurvey[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const selected = roles.find((r) => r.key === form.role);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFields({});
    setError('');
    try {
      const maxUses = form.maxUses.trim() === '' ? null : Number(form.maxUses);
      const expiresAt = form.expiresAt ? new Date(form.expiresAt).toISOString() : null;
      onCreated(await api.createInvitation({ label: form.label, role: form.role, maxUses, expiresAt, surveys: withAccess(surveys, form.role) }));
    } catch (err) {
      if (err instanceof ApiError) {
        setFields(err.fields);
        if (!Object.keys(err.fields).length) setError(err.message);
      }
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('invitations.add')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" form="create-invitation" disabled={busy}>
            {busy ? t('invitations.creating') : t('invitations.create')}
          </button>
        </>
      }
    >
      <form id="create-invitation" className="form-grid" onSubmit={submit}>
        {error && <div className="banner error">{error}</div>}
        <Field label={t('invitations.label')} hint={t('invitations.label_hint')}>
          <input className="input" value={form.label} placeholder={t('invitations.label_placeholder')} onChange={(e) => setForm({ ...form, label: e.target.value })} />
        </Field>
        <Field label={t('users.role')} error={fields.role} hint={`${t('invitations.role_hint')} ${selected ? roleDescription(t, selected) : ''}`.trim()}>
          <select className="select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
            {roles.map((r) => (
              <option key={r.key} value={r.key}>
                {roleLabel(t, r.key, r.name)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('invitations.max_uses')} error={fields.maxUses} hint={t('invitations.max_uses_hint')}>
          <input className="input" type="number" min={1} step={1} value={form.maxUses} onChange={(e) => setForm({ ...form, maxUses: e.target.value })} />
        </Field>
        <Field label={t('invitations.expires_at')} error={fields.expiresAt} hint={t('invitations.expires_hint')}>
          <input className="input" type="datetime-local" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
        </Field>
        <SurveyPicker role={form.role} value={surveys} onChange={setSurveys} error={fields.surveys} />
      </form>
    </Modal>
  );
}

/** Enumerator invitations add people as enumerators; other roles as viewers unless set to editor. */
function withAccess(list: InvitationSurvey[], role: Role): InvitationSurvey[] {
  return list.map((x) => ({
    surveyId: x.surveyId,
    access: role === 'enumerator' ? 'enumerator' : x.access === 'editor' ? 'editor' : 'viewer',
  }));
}

/** Questionnaires people join once their sign-up is approved. */
function SurveyPicker({ role, value, onChange, error }: { role: Role; value: InvitationSurvey[]; onChange: (v: InvitationSurvey[]) => void; error?: string }) {
  const { t } = useI18n();
  const [all, setAll] = useState<SurveySummary[] | null>(null);
  useEffect(() => {
    api.listSurveys({}).then((r) => setAll(r.surveys.filter((s) => s.status !== 'archived')), () => setAll([]));
  }, []);
  const enumerator = role === 'enumerator';
  const picked = (id: string) => value.find((x) => x.surveyId === id);
  const toggle = (id: string) =>
    onChange(picked(id) ? value.filter((x) => x.surveyId !== id) : [...value, { surveyId: id, access: enumerator ? 'enumerator' : 'viewer' }]);
  const setAccess = (id: string, access: InvitationSurveyAccess) => onChange(value.map((x) => (x.surveyId === id ? { ...x, access } : x)));

  return (
    <Field label={t('invitations.surveys')} error={error} hint={enumerator ? t('invitations.surveys_hint_enumerator') : t('invitations.surveys_hint_other')}>
      {all === null ? (
        <PageLoader />
      ) : all.length === 0 ? (
        <p className="small muted">{t('invitations.surveys_none')}</p>
      ) : (
        <div className="pick-list" role="group" aria-label={t('invitations.surveys')} style={{ maxHeight: 220, overflow: 'auto' }}>
          {all.map((s) => {
            const p = picked(s.id);
            return (
              <label key={s.id} className={`pick-item ${p ? 'on' : ''}`}>
                <input type="checkbox" checked={!!p} onChange={() => toggle(s.id)} />
                <span className="grow ellipsis">{s.title || t('common.untitled_questionnaire')}</span>
                {p && !enumerator && (
                  <select className="select" style={{ width: 'auto', height: 30, padding: '2px 8px' }} value={p.access === 'editor' ? 'editor' : 'viewer'} onChange={(e) => setAccess(s.id, e.target.value as InvitationSurveyAccess)}>
                    <option value="viewer">{t('invitations.access_viewer')}</option>
                    <option value="editor">{t('invitations.access_editor')}</option>
                  </select>
                )}
              </label>
            );
          })}
        </div>
      )}
    </Field>
  );
}

function EditInvitation({ invitation, onClose, onSaved }: { invitation: Invitation; onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const [label, setLabel] = useState(invitation.label);
  const [maxUses, setMaxUses] = useState(invitation.maxUses == null ? '' : String(invitation.maxUses));
  const [expiresAt, setExpiresAt] = useState(toLocalInput(invitation.expiresAt));
  const [surveys, setSurveys] = useState<InvitationSurvey[]>(invitation.surveys);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFields({});
    setError('');
    try {
      await api.updateInvitation(invitation.id, {
        label,
        maxUses: maxUses.trim() === '' ? 0 : Number(maxUses),
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : { clearExpiry: true }),
        surveys: withAccess(surveys, invitation.role),
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        setFields(err.fields);
        if (!Object.keys(err.fields).length) setError(err.message);
      }
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('invitations.edit_title')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" form="edit-invitation" disabled={busy}>
            {t('invitations.save')}
          </button>
        </>
      }
    >
      <form id="edit-invitation" className="form-grid" onSubmit={submit}>
        {error && <div className="banner error">{error}</div>}
        <Field label={t('invitations.label')}>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <Field label={t('invitations.max_uses')} error={fields.maxUses} hint={t('invitations.max_uses_hint')}>
          <input className="input" type="number" min={1} step={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
        </Field>
        <Field label={t('invitations.expires_at')} error={fields.expiresAt} hint={t('invitations.expires_hint')}>
          <input className="input" type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </Field>
        <SurveyPicker role={invitation.role} value={surveys} onChange={setSurveys} error={fields.surveys} />
      </form>
    </Modal>
  );
}

/** Sign-ups from one link: approve or reject them one by one, by selection, or all at once. */
function Signups({ invitation, onClose, onChanged }: { invitation: Invitation; onClose: () => void; onChanged: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [users, setUsers] = useState<User[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.invitationSignups(invitation.id).then(
      (r) => setUsers(r.users),
      (e) => toast(e instanceof ApiError ? e.message : t('invitations.load_failed'), 'error'),
    );
  }, [invitation.id, toast, t]);
  useEffect(load, [load]);

  const waiting = users?.filter((u) => u.pending) ?? [];

  async function decide(decision: 'approve' | 'reject', userIds?: string[]) {
    const n = userIds?.length ?? waiting.length;
    if (decision === 'reject' && !confirmAction(t('invitations.confirm_reject', { n }))) return;
    setBusy(true);
    try {
      const r = await api.decideSignups(invitation.id, decision, userIds);
      toast(t(decision === 'approve' ? 'invitations.approved_n' : 'invitations.rejected_n', { n: r.users.length }));
      setSelected(new Set());
      load();
      onChanged();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('invitations.update_failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const picked = [...selected];
  return (
    <Modal
      title={t('invitations.signups_title', { label: invitation.label || t('invitations.untitled') })}
      onClose={onClose}
      width={720}
      footer={
        waiting.length > 0 ? (
          <>
            <button className="btn danger" disabled={busy || picked.length === 0} onClick={() => void decide('reject', picked)}>
              <X /> {t('invitations.reject_selected')}
            </button>
            <button className="btn" disabled={busy || picked.length === 0} onClick={() => void decide('approve', picked)}>
              <Check /> {t('invitations.approve_selected')}
            </button>
            <button className="btn primary" disabled={busy} onClick={() => void decide('approve')}>
              <Check /> {t('invitations.approve_all', { n: waiting.length })}
            </button>
          </>
        ) : undefined
      }
    >
      {users === null ? (
        <PageLoader />
      ) : users.length === 0 ? (
        <p className="muted">{t('invitations.signups_none')}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  {waiting.length > 0 && (
                    <input
                      type="checkbox"
                      aria-label={t('invitations.select_all')}
                      title={t('invitations.select_all')}
                      checked={picked.length === waiting.length}
                      onChange={(e) => setSelected(new Set(e.target.checked ? waiting.map((u) => u.id) : []))}
                    />
                  )}
                </th>
                <th>{t('users.col_user')}</th>
                <th>{t('users.col_status')}</th>
                <th>{t('users.col_created')}</th>
                <th aria-label={t('surveys.col_actions')} />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.pending && <input type="checkbox" aria-label={u.email} checked={selected.has(u.id)} onChange={() => toggle(u.id)} />}</td>
                  <td>
                    <div className="row gap-12">
                      <Avatar user={u} size={30} />
                      <div>
                        <div className="title-cell">{u.name || '—'}</div>
                        <div className="muted">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {u.pending ? (
                      <span className="pill dot orange">{t('invitations.signup_waiting')}</span>
                    ) : (
                      <span className="pill dot green">{t('invitations.signup_approved')}</span>
                    )}
                  </td>
                  <td className="muted">{formatDate(u.createdAt)}</td>
                  <td className="num">
                    {u.pending && (
                      <div className="row gap-8" style={{ justifyContent: 'flex-end' }}>
                        <button className="btn sm icon" disabled={busy} aria-label={t('invitations.reject_selected')} title={t('invitations.reject_selected')} onClick={() => void decide('reject', [u.id])}>
                          <X />
                        </button>
                        <button className="btn sm icon primary" disabled={busy} aria-label={t('invitations.approve_selected')} title={t('invitations.approve_selected')} onClick={() => void decide('approve', [u.id])}>
                          <Check />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
